// @flow

import type { AuthBloc } from "../../blocs/authBloc";
import type ProjectGroup from "../../models/ProjectGroup/ProjectGroup";
import { AppToaster } from "../../components/Toaster/Toaster";
import { Intent } from "@blueprintjs/core";
import { apiAxios } from "../../config/AxiosConfig";
import {
  CreateProjectGroupRequest,
  UpdateProjectGroupRequest,
} from "../../models/ProjectGroup/ProjectGroup";
import Project from "../../models/Project/Project";
import history from "../../history";

class ProjectGroupApi {
  authBloc: AuthBloc;

  constructor(authBloc: AuthBloc) {
    this.authBloc = authBloc;
  }

  async getAll(projectStates): Promise<Array<ProjectGroup>> {
    const tenantId = this.authBloc.getCurrentTenant();
    const response = await apiAxios.get(
      `/tenants/${tenantId}/project-groups?status=${projectStates ? projectStates:[]}`,
      this.buildHeaders()
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.status);
    }
  }

  async cloneProject(
    projectId: any
  ): Promise<ProjectGroup> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(
        `/tenants/${tenantId}/projects/clone`,
        { "parentProjectId": projectId},
        {...this.buildHeaders(), skipErrorToast: true}
      );

      if (response.status === 201) {
        // AppToaster.show({
        //   message: "Project Cloned Successfully",
        //   intent: Intent.PRIMARY,
        // });
        localStorage.setItem("isCloneInprogress", true);
        return response.data;
      } else {
        localStorage.setItem("isCloneInprogress", false);
      }
    } catch (error) {
      localStorage.setItem("isCloneInprogress", false);
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to clone project. Please try again.',
        intent: Intent.DANGER,
      });
    }
  }

  async createProjectGroup(
    createProjectGroupRequest: CreateProjectGroupRequest
  ): Promise<ProjectGroup> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.post(
        `/tenants/${tenantId}/project-groups`,
        createProjectGroupRequest,
        {...this.buildHeaders(), skipErrorToast: true}
      );

      if (response.status === 201) {
        history.goBack();
        setTimeout(() => {
          AppToaster.show({
            message: "Project Group Created",
            intent: Intent.PRIMARY,
          });
        }, 300);

        return response.data;
      } else {
      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to create project group. Please try again.',
        intent: Intent.DANGER,
      });
    }
  }

  async addProjectToGroup(
    projectGroupToAddTo: ProjectGroup,
    projectToAdd: Project
  ): Promise<ProjectGroup> {
    const tenantId = this.authBloc.getCurrentTenant();

    const response = await apiAxios.post(
      `/tenants/${tenantId}/project-groups/${projectGroupToAddTo.id}/projects`,
      projectToAdd,
      this.buildHeaders()
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(response.status);
    }
  }

  async updateProjectGroup(
    updateProjectGroupRequest: UpdateProjectGroupRequest
  ): Promise<ProjectGroup> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      let requestURL;

      if(updateProjectGroupRequest.closeProjects){
        requestURL = `/tenants/${tenantId}/project-groups/${updateProjectGroupRequest.id}?closeProjects=${updateProjectGroupRequest.closeProjects && updateProjectGroupRequest.closeProjects}`
      }else{
        requestURL = `/tenants/${tenantId}/project-groups/${updateProjectGroupRequest.id}`
      }

      const response = await apiAxios.patch(
        requestURL,
        updateProjectGroupRequest,
        {...this.buildHeaders(), skipErrorToast: true}
      );

      if (response.status === 204) {
        if(updateProjectGroupRequest.closeProjects){
        }else{
          history.goBack();
        }
        setTimeout(() => {
          AppToaster.show({
            message: "Project Group Updated",
            intent: Intent.PRIMARY,
          });
        }, 300);

        return response.data;
      } else {

      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to update project group. Please try again.',
        intent: Intent.DANGER,
      });
    }
  }

  async removeProjectGroup(projectGroupId: string): Promise<ProjectGroup> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();

      const response = await apiAxios.delete(
        `/tenants/${tenantId}/project-groups/${projectGroupId}`,
        {...this.buildHeaders(), skipErrorToast: true}
      );

      if (response.status === 200) {
        AppToaster.show({
          message: "Project Group Deleted",
          intent: Intent.PRIMARY,
        });
        return response.data;
      } else {

      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to delete project group. Please try again.',
        intent: Intent.DANGER,
      });
    }
  }

  async exportProjectGroup(projectGroupId: string): Promise<ProjectGroup> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();
      const response = await apiAxios.get(
        `/tenants/${tenantId}/project-groups/${projectGroupId}/projects/export-data`,
        {...this.buildHeaders(), skipErrorToast: true}
      );
      if (response.status === 200) {
        return response.data;
      } else {

      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to export project group. Please try again.',
        intent: Intent.DANGER
      });
    }
  }

  async editProjectGroup(projectGroupId: string): Promise<ProjectGroup> {
    try {
      const tenantId = this.authBloc.getCurrentTenant();
      const response = await apiAxios.get(
        `/tenants/${tenantId}/project-groups/${projectGroupId}/default-tasks`,
        {...this.buildHeaders(), skipErrorToast: true}
      );
      if (response.status === 200) {
        return response.data;
      } else {

      }
    } catch (error) {
      AppToaster.show({
        message: error.response?.data?.message || 'Failed to load project group. Please try again.',
        intent: Intent.DANGER
      });
    }
  }


  buildHeaders = () => {
    const accessToken = this.authBloc.getCurrentToken();
    const auth = "Bearer " + accessToken;
    return { headers: { Authorization: auth } };
  };
}

export default ProjectGroupApi;
